import { Link } from "react-router-dom";
import { useState } from "react";
import employeeService from "../../api/services/employeeService";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import SafeImage from "../../components/common/SafeImage";

export default function EmployeeList() {
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("name"); // 'name', 'id', or 'email'
  const [statusUpdating, setStatusUpdating] = useState({});
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Fetch employees using TanStack Query
  const { data, isLoading, isError } = useQuery({
    queryKey: ["employees", page, perPage],
    queryFn: async () => {
      const result = await employeeService.getAllEmployees(page, perPage);
      if (!result.success) {
        throw new Error(result.message || "Failed to fetch employees");
      }
      // Transform API data to match the expected format
      return {
        employees: result.data.map((employee) => ({
          Id: employee.employee_id,
          name: employee.user.name,
          email: employee.user.email,
          phone: employee.user.phone_no || "N/A",
          company: "GoHome Express",
          image: getProfileImageUrl(employee.user.profile_image),
          position: employee.designation,
          status: employee.user.status,
          dateOfJoining: employee.created_at,
        })),
        meta: result.meta,
      };
    },
    onError: (error) => {
      toast.error(error.message || "Failed to fetch employees");
    },
  });

  const employees = data?.employees || [];
  const meta = data?.meta || {};
  const totalPages = meta.last_page || 1;

  // Delete employee mutation
  const deleteEmployeeMutation = useMutation({
    mutationFn: (employeeId) => employeeService.deleteEmployee(employeeId),
    onSuccess: (result, employeeId) => {
      toast.success("Employee deleted successfully");
      // Invalidate and refetch
      queryClient.invalidateQueries(["employees"]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete employee");
    },
  });

  // Update employee status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ employeeId, newStatus }) => {
      console.log("Mutation function called:", { employeeId, newStatus });
      return employeeService.updateEmployee(employeeId, { status: newStatus });
    },
    onSuccess: (result, { employeeId, newStatus }) => {
      console.log("Mutation success:", result);
      toast.success("Status updated!");
      // Optimistically update the UI
      queryClient.setQueryData(["employees", page, perPage], (old) => {
        if (!old) return old;
        return {
          ...old,
          employees: old.employees.map((e) =>
            e.Id === employeeId ? { ...e, status: newStatus } : e,
          ),
        };
      });
    },
    onError: (error, variables) => {
      console.log("Mutation error:", error);
      toast.error(error.message || "Failed to update status");
      if (variables && variables.employeeId) {
        setStatusUpdating((prev) => ({
          ...prev,
          [variables.employeeId]: false,
        }));
      }
    },
    onSettled: (data, error, variables) => {
      // Wait for the employees query to refetch before hiding the spinner
      queryClient.refetchQueries(["employees"]).then(() => {
        if (variables && variables.employeeId) {
          setStatusUpdating((prev) => ({
            ...prev,
            [variables.employeeId]: false,
          }));
        }
      });
    },
  });

  // Helper to get full image URL
  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) {
      return "/avatar-placeholder.png"; // Default image from public folder
    }
    if (profileImage.startsWith("/storage/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") + profileImage
      );
    }
    return profileImage;
  };

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!employeeToDelete) return;
    await deleteEmployeeMutation.mutateAsync(employeeToDelete.Id);
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  // Handle status change
  const handleStatusChange = async (employee, newStatus) => {
    console.log("Status change triggered:", {
      employeeId: employee.Id,
      newStatus,
    });
    setStatusUpdating((prev) => ({ ...prev, [employee.Id]: true }));
    await updateStatusMutation.mutateAsync({
      employeeId: employee.Id,
      newStatus,
    });
  };

  // Check if a specific employee is being updated
  const isEmployeeUpdating = (employeeId) => {
    return statusUpdating[employeeId] || false;
  };

  // Filter employees by status and search query (on current page only)
  const filteredEmployees = employees.filter((emp) => {
    const matchesStatus = statusFilter === "all" || emp.status === statusFilter;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return matchesStatus;

    let matches = false;
    if (filterType === "name") {
      matches = emp.name.toLowerCase().includes(query);
    } else if (filterType === "id") {
      matches = String(emp.Id) === query;
    } else if (filterType === "email") {
      matches = emp.email.toLowerCase().includes(query);
    }
    return matchesStatus && matches;
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading employees...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body text-center">
                <p className="text-danger">
                  Error loading employees. Please try again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* All Employes Table Start */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="title-header option-title d-flex align-items-center justify-content-between mb-4">
              <h5>All Employees</h5>
              <div className="d-flex align-items-center">
                <select
                  className="form-select form-select-sm w-auto me-2"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="name">Name</option>
                  <option value="id">ID</option>
                  <option value="email">Email</option>
                </select>
                <input
                  type="text"
                  className="form-control form-control-sm me-2"
                  style={{ width: 220 }}
                  placeholder={`Search by ${filterType}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <form className="d-inline-flex me-2">
                  <Link
                    to="/employeeaddorupdate"
                    className="align-items-center btn btn-theme d-flex"
                  >
                    <i data-feather="plus"></i>Add Employee
                  </Link>
                </form>
                <select
                  className="form-select form-select-sm w-auto"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {filteredEmployees.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No employees found</p>
              </div>
            ) : (
              <>
                <div className="table-responsive table-product">
                  <table
                    className="table all-package theme-table align-middle"
                    id="table_id"
                  >
                    <thead className="table-light">
                      <tr>
                        <th className="text-center">ID</th>
                        <th className="text-center">Employee</th>
                        <th className="text-start">Name</th>
                        <th className="text-start">Phone</th>
                        <th className="text-strat">Email</th>
                        <th className="text-center">Option</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredEmployees.map((employee, keynum) => (
                        <tr key={keynum}>
                          <td className="text-center">
                            <div className="user-id">
                              <span>{employee.Id}</span>
                            </div>
                          </td>

                          <td className="text-center">
                            <div className="table-image">
                              <SafeImage
                                src={employee.image}
                                className="img-fluid "
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                                alt={employee.name}
                              />
                            </div>
                          </td>

                          <td>
                            <div className="user-name">
                              <span className="d-block fw-bold">
                                {employee.name}
                              </span>
                              <span className="text-muted small">
                                {employee.company}
                              </span>
                            </div>
                          </td>

                          <td className="text-start">{employee.phone}</td>

                          <td className="text-start">{employee.email}</td>

                          <td
                            className="text-center"
                            style={{ padding: "4px" }}
                          >
                            <ul
                              className="list-inline mb-0 d-flex align-items-center justify-content-center"
                              style={{ gap: "4px" }}
                            >
                              <li className="list-inline-item">
                                <Link
                                  to={`/employeedetails/${employee.Id}`}
                                  className="btn btn-sm btn-light p-1"
                                  style={{ lineHeight: 1, minWidth: 0 }}
                                >
                                  <i
                                    className="ri-eye-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  ></i>
                                </Link>
                              </li>
                              <li className="list-inline-item">
                                <Link
                                  to={`/employeeaddorupdate/${employee.Id}`}
                                  className="btn btn-sm btn-light p-1"
                                  style={{ lineHeight: 1, minWidth: 0 }}
                                >
                                  <i
                                    className="ri-pencil-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                    }}
                                  ></i>
                                </Link>
                              </li>
                              <li className="list-inline-item">
                                <button
                                  onClick={() => handleDeleteClick(employee)}
                                  className="btn btn-sm btn-light p-1"
                                  style={{
                                    border: "none",
                                    background: "none",
                                    padding: 0,
                                    lineHeight: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <i
                                    className="ri-delete-bin-line"
                                    style={{
                                      cursor: "pointer",
                                      fontSize: "16px",
                                      color: "#dc3545",
                                      verticalAlign: "middle",
                                    }}
                                  ></i>
                                </button>
                              </li>
                            </ul>
                          </td>

                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center">
                              <select
                                className={`form-select form-select-sm w-auto ${employee.status === "active" ? "border-success" : "border-danger"} ${isEmployeeUpdating(employee.Id) ? "opacity-50" : ""}`}
                                value={employee.status}
                                disabled={isEmployeeUpdating(employee.Id)}
                                onChange={(e) =>
                                  handleStatusChange(employee, e.target.value)
                                }
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                              {isEmployeeUpdating(employee.Id) && (
                                <span
                                  className="spinner-border spinner-border-sm ms-2 text-primary"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination Controls */}
                <nav className="d-flex justify-content-center mt-4">
                  <ul className="pagination">
                    <li className={`page-item${page === 1 ? " disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      >
                        &laquo; Prev
                      </button>
                    </li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (pg) => (
                        <li
                          key={pg}
                          className={`page-item${pg === page ? " active" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pg)}
                          >
                            {pg}
                          </button>
                        </li>
                      ),
                    )}
                    <li
                      className={`page-item${page === totalPages ? " disabled" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      >
                        Next &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete employee "
                  {employeeToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteEmployeeMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleteEmployeeMutation.isLoading}
                >
                  {deleteEmployeeMutation.isLoading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
